import React, { useEffect, useRef, useState } from "react"
import moment from "moment"
import { useTheme } from "@emotion/react"
import { styles } from "../../resources/styles/Styles"
import SwitchText from "../SwitchText/SwitchText"
import { PatientProfileCardStyles } from "./PatientProfileCardStyles"
import PermissionUtils from "../../core/store/utils/PermissionUtils"
import "./PatientProfileCard.scss"
import { MocUserRole } from "core/services/models/Enums"
import SaveAsIcon from "@mui/icons-material/SaveAs"
import Toast from "components/Toast"
import DatePicker from "react-datepicker"
import { addNewPatient } from "core/services/api"

interface PatientProfileCardProps {
  patientDetail: any | undefined
  userRole: any
  isActive: any
  isAlive: any
  setActive: any
  setAlive: any
  isFollowup: any
  setFollowup: any
  isReadOnly?: any
  isCalledFromDirectory?: any
  setPatientDod?: any
}

function PatientProfileCard({
  userRole,
  patientDetail,
  isActive,
  isAlive,
  isFollowup,
  setActive,
  setAlive,
  setFollowup,
  isReadOnly,
  isCalledFromDirectory,
  setPatientDod
}: PatientProfileCardProps) {
  const [consultantNotes, setConsultantNotes] = useState<any>(patientDetail?.consultantNotes)
  const [displayToast, toggleDisplaytoast] = useState<any>()
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false)

  const theme = useTheme()
  const globalStyles = styles(theme)
  const classes = PatientProfileCardStyles(theme)
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleSave(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    const temp = { ...patientDetail };
    temp['consultantNotes'] = consultantNotes;
    delete temp["securityDeposit"]
    delete temp["totalPendingAmount"]
    delete temp["totalPendingInvoices"]
    delete temp["isEditedAlready"]
    delete temp["oldDocuments"]
    addNewPatient(temp)
      .then(() => {
        toggleDisplaytoast({
          type: "success",
          text: "Notes Saved Successfully"
        })
      })
      .catch((err) => {
        toggleDisplaytoast({
          type: "error",
          text: err?.response?.data?.message
            ? err?.response?.data?.message
            : "Unable to save notes at the moment. Please try again after sometime",
        })
      })
  }

  function adjustTextareaHeight() {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '20px'; // reset the height to auto to avoid overflow
      textarea.style.height = `${textarea.scrollHeight}px`; // set the height to the scroll height

      const rows = Math.ceil(textarea.scrollHeight / parseInt(getComputedStyle(textarea).lineHeight || '0'));
      textarea.rows = rows;
    }
  }

  const handleInput = (event: any) => {
    const value = event.target.value;
    setConsultantNotes(value);
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [consultantNotes]);

  return (
    <div className={`${classes.card} ${classes.pt21}`}>
      <div
        className={`col-md-12 row m-0 ${globalStyles.alignItemsCenter} ${classes.cardPaddingSection} ${classes.btmBorder}`}
      >
        <div className="col-md-5 p-0">
          <div className={`d-flex ${globalStyles.alignItemsCenter}`}>
            <div className={`${classes.patientName} p-0`}>
              {PermissionUtils.displayPatientName(
                userRole,
                patientDetail,
                true,
                null
              )}
            </div>
            <div className={classes.pl40}>
              <div title={patientDetail?.reasonForInactivatingAccount}>
                {userRole === MocUserRole.PATIENT ? (
                  ""
                ) : (
                  <SwitchText
                    onToggle={(evt: any) =>
                      userRole === MocUserRole.FRONT_DESK ? setActive(evt) : ""
                    }
                    title={isActive ? "Active" : "InActive"}
                    status={isActive}
                  />
                )}
                <div>
                  <span>
                    {patientDetail?.accountInactivatedOn
                      ? moment(patientDetail?.accountInactivatedOn).format("DD-MM-YYYY")
                      : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* {reasonForInactivatingAccount}  */}
        </div>
        {userRole === MocUserRole.FRONT_DESK && (
          <div className="col-md-2 p-0" style={{ marginTop: "-23px" }}>
            <label>Lost to Followup</label>
            <div title={patientDetail?.lostToFollowUpReason}>
              <SwitchText
                onToggle={(evt: any) => setFollowup(evt)}
                title={isFollowup ? "Yes" : "No"}
                status={isFollowup}
              />
              <span>
                {patientDetail?.followedUpDate
                  ? patientDetail?.followedUpDate
                  : ""}
              </span>
            </div>
          </div>
        )}

        <div className="col-md-5 p-0">
          <div className={`${globalStyles.flexRowRight} text-truncate `}>
            <div
              className={`${globalStyles.flexRow} ${globalStyles.alignItemsCenter} ${classes.phone} col-5 p-0`}
            >
              <img
                className={classes.phoneIcon}
                src={"../../assets/images/phoneIcon.svg"}
                alt="phoneIcon"
              />
              <div
                className={`${classes.phoneText} text-truncate `}
                title={patientDetail?.phoneNumber}

                style={{ "paddingLeft": "0px" }}
              >
                {patientDetail?.phoneNumber}
              </div>
            </div>

            {patientDetail?.email ? (
              <div
                className={`${globalStyles.flexRow} ${globalStyles.alignItemsCenter} ${classes.email} col-md-7 p-0`}
              >
                <img
                  className={classes.emailIcon}
                  src={"../../assets/images/emailIcon.svg"}
                  alt="emailIcon"
                />
                <div
                  className={`${classes.emailText} text-truncate`}
                  title={patientDetail?.email}
                >
                  {patientDetail?.email}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className={`col-12 row m-0`} id="cardPaddingSection">
        <div className="col-3">
          <div className="title">Gender:</div>
          <div className="title">Birthday:</div>
          {userRole !== MocUserRole.PATIENT && (
            <div className="title text-truncate">Category:</div>
          )}
          <div className="title">Registered Date:</div>
          {userRole !== MocUserRole.PATIENT && (
            <div className="title">Old Patient ID:</div>
          )}
          {userRole !== MocUserRole.PATIENT && (
            <div className="title sur-status">Survival Status:</div>
          )}
          {userRole === MocUserRole.CONSULTANT && (
            <div className="title">Notes:</div>
          )}
          {/* nut,rmo,gentic coun, */}
        </div>
        <div className="col-md-3">
          <div className="value" title={patientDetail?.gender}>
            {patientDetail?.gender ? patientDetail?.gender : "-"}
          </div>
          <div className="value" title={patientDetail?.dob && moment(patientDetail?.dob).format("DD-MM-YYYY")}>
            {patientDetail?.dob
              ? moment(patientDetail?.dob).format("DD-MM-YYYY")
              : "-"}
          </div>
          {userRole !== MocUserRole.PATIENT && (
            <div className="value" title={patientDetail?.category}>{patientDetail?.category}</div>
          )}
          <div className="value" title={patientDetail?.dateOfRegistration && moment(patientDetail?.dateOfRegistration).format("DD-MM-YYYY")}>
            {patientDetail?.dateOfRegistration
              ? moment(patientDetail?.dateOfRegistration).format("DD-MM-YYYY")
              : "-"}
          </div>
          {userRole !== MocUserRole.PATIENT && (
            <div className="value" title={patientDetail?.oldMocId}>{patientDetail?.oldMocId ? patientDetail?.oldMocId : "-"}</div>
          )}
          {userRole !== MocUserRole.PATIENT && (
            <div className="value">
              <SwitchText
                title={isAlive ? "Alive" : "Deceased"}
                status={isAlive}
                onToggle={(evt: any) => {
                  if (userRole === MocUserRole.FRONT_DESK) {
                    setAlive(evt);
                  }
                }}
              />
              {patientDetail?.survivalStatus?.toLowerCase() === "deceased" && (
                <div className="value survival-status">
                  {" "}
                  {moment(patientDetail?.dod).format("DD-MM-YYYY")}
                  <img
                    className="datepicker cursor-pointer"
                    alt="datepicker icon"
                    src="../../assets/images/datepickerIcon.svg"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  />
                  {showDatePicker && <DatePicker
                    selected={patientDetail?.dod ? new Date(patientDetail?.dod) : new Date()}
                    onChange={(event: any) => {
                      setPatientDod(moment(event).format("YYYY-MM-DD"))
                      setShowDatePicker(!showDatePicker)
                    }}
                    onClickOutside={() => setShowDatePicker(!showDatePicker)}
                    open={showDatePicker}
                    maxDate={new Date()}
                    minDate={patientDetail?.lastVisit ? new Date(patientDetail?.lastVisit) : new Date(patientDetail?.dateOfRegistration)}
                  />}
                </div>
              )}
            </div>
          )}
          <div className="d-flex">
            {userRole === MocUserRole.CONSULTANT && (
              <>
                <textarea
                  ref={textareaRef}
                  className="CONSULTANTNOTES"
                  name="message"
                  onChange={handleInput}
                  value={consultantNotes}
                  disabled={isReadOnly}
                ></textarea>
                {
                  !isReadOnly && consultantNotes !== "" && (
                    <div
                      className="cursor-pointer"
                    >
                      <SaveAsIcon
                        className="icon"
                        color="primary"
                        onClick={(e: any) => handleSave(e)}
                      />
                    </div>
                  )
                }
              </>
            )}
          </div>
        </div>

        <div className="col-4">
          <div className="title">Referred By:</div>
          <div className="title">Blood Group:</div>
          <div className="title">Responsible Person Name:</div>
          <div className="title">Responsible Person Contact:</div>
          {userRole !== MocUserRole.PATIENT && (
            <div className="title">Send Message:</div>
          )}
        </div>
        <div className="col-2">
          <div className="value" title={patientDetail?.referredBy?.doctorName}>
            {patientDetail?.referredBy?.doctorName
              ? patientDetail?.referredBy?.doctorName
              : "-"}
          </div>
          <div className="value">
            {patientDetail?.bloodGroup ? patientDetail?.bloodGroup : "-"}
          </div>
          <div className="value" title={patientDetail?.responsiblePersonName}>
            {patientDetail?.responsiblePersonName
              ? patientDetail?.responsiblePersonName
              : "-"}
          </div>
          <div className={`${isCalledFromDirectory ? 'value resp-wrap' : 'value'}`} title={patientDetail?.responsiblePersonPhoneNumber}>
            {patientDetail?.responsiblePersonPhoneNumber
              ? patientDetail?.responsiblePersonPhoneNumber
              : "-"}
          </div>
          {userRole !== MocUserRole.PATIENT && (
            <div className={classes.title ? `${classes.title} value sms-value` : 'value sms-value'}>SMS</div>
          )}
        </div>
        {displayToast && (
          <Toast
            textMessage={displayToast?.text}
            type={displayToast?.type}
            onClose={(e: any) => toggleDisplaytoast(false)}
            onCloseText="Close"
            onClickText={null}
          />
        )}
      </div>
    </div>
  )
}

export default PatientProfileCard
